import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { Loader } from '@/components/atoms/Loader';
import { getPokedexLeaderboard } from '@/services/pokemon';
import { getPrestigeLeaderboard } from '@/services/user';
import { useMeUser } from '@/hooks/useUser';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

const RankBadge = ({ rank }: { rank: number }) =>
  MEDAL[rank] ? (
    <span className="text-lg leading-none">{MEDAL[rank]}</span>
  ) : (
    <span className="font-lexend text-muted w-7 text-center text-sm font-semibold">
      {rank}
    </span>
  );

// ─── Pokédex Leaderboard ──────────────────────────────────────────────────────

const PokedexLeaderboard = ({ currentUsername }: { currentUsername?: string }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['leaderboard-pokedex'],
    queryFn: getPokedexLeaderboard,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader size={40} color="#f29602" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="text-4xl">📡</p>
        <p className="text-content/60 text-sm">Classement indisponible pour le moment.</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-4xl">🔍</p>
        <p className="text-content/50 text-sm">Aucune entrée pour l'instant.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {data.map(entry => {
        const isMe = entry.username === currentUsername;
        return (
          <div
            key={entry.rank}
            className={`flex items-center gap-3 rounded-2xl border bg-surface px-4 py-3 shadow-sm transition-shadow hover:shadow-md ${
              isMe ? 'border-primary-300' : 'border-transparent'
            }`}
          >
            <RankBadge rank={entry.rank} />
            <p
              className={`font-lexend min-w-0 flex-1 truncate font-semibold ${
                isMe ? 'text-primary-500' : 'text-content'
              }`}
            >
              {entry.username}
              {isMe && (
                <span className="ml-2 rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-bold text-primary-500">
                  Moi
                </span>
              )}
            </p>
            <div className="text-right">
              <p className="font-lexend text-content font-bold">{entry.pokedexCount}</p>
              <p className="text-content/40 text-xs">entrées</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Prestige Leaderboard ─────────────────────────────────────────────────────

const PRESTIGE_CRITERIA = [
  { label: 'Expéditions', detail: 'Pondérées par rareté (×0.02 à ×1.5)' },
  { label: 'Pokédex', detail: 'Entrées ×1.5' },
  { label: 'Niveau 75+', detail: 'Pokémon ×3' },
  { label: 'IV ≥ 25', detail: 'Pokémon IV moyen ×5' },
  { label: 'Bonheur', detail: 'Pokémon bonheur >100 ×0.5' },
];

const PrestigeLeaderboard = ({ currentUsername }: { currentUsername?: string }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['leaderboard-prestige'],
    queryFn: getPrestigeLeaderboard,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      {/* Info block */}
      <div className="rounded-2xl border border-line-soft bg-surface">
        <button
          onClick={() => setShowInfo(v => !v)}
          className="flex w-full items-center gap-2 px-4 py-3 text-left"
        >
          <Info size={14} className="text-content/40 shrink-0" />
          <span className="text-content/60 flex-1 text-sm">
            Comment est calculé le score de prestige ?
          </span>
          <span className="text-content/40 text-xs">{showInfo ? '▲' : '▼'}</span>
        </button>
        {showInfo && (
          <div className="border-t border-line-soft px-4 pb-4">
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {PRESTIGE_CRITERIA.map(c => (
                <div key={c.label} className="rounded-xl bg-raised px-3 py-2">
                  <p className="text-content text-sm font-semibold">{c.label}</p>
                  <p className="text-content/50 text-xs">{c.detail}</p>
                </div>
              ))}
            </div>
            <p className="text-content/40 mt-3 text-xs">
              Score final normalisé sur 1 000 points.
            </p>
          </div>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader size={40} color="#f29602" />
        </div>
      ) : isError || !data ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-4xl">📡</p>
          <p className="text-content/60 text-sm">Classement indisponible pour le moment.</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-4xl">🔍</p>
          <p className="text-content/50 text-sm">Aucune entrée pour l'instant.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {data.map(entry => {
            const isMe = entry.username === currentUsername;
            return (
              <div
                key={entry.rank}
                className={`rounded-2xl border bg-surface px-4 py-3 shadow-sm transition-shadow hover:shadow-md ${
                  isMe ? 'border-primary-300' : 'border-transparent'
                }`}
              >
                <div className="flex items-center gap-3">
                  <RankBadge rank={entry.rank} />
                  <p
                    className={`font-lexend min-w-0 flex-1 truncate font-semibold ${
                      isMe ? 'text-primary-500' : 'text-content'
                    }`}
                  >
                    {entry.username}
                    {isMe && (
                      <span className="ml-2 rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-bold text-primary-500">
                        Moi
                      </span>
                    )}
                  </p>
                  <div className="text-right">
                    <p className="font-lexend text-content font-bold">
                      {Math.round(entry.score)}{' '}
                      <span className="text-content/40 text-xs font-normal">/ 1000</span>
                    </p>
                  </div>
                </div>

                {/* Detail row */}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 pl-10">
                  <span className="text-content/50 text-xs">
                    Expéd.&nbsp;<strong className="text-content">{Math.round(entry.expeditionScore)}</strong>
                  </span>
                  <span className="text-content/50 text-xs">
                    Pokédex&nbsp;<strong className="text-content">{entry.pokedexEntries}</strong>
                  </span>
                  <span className="text-content/50 text-xs">
                    Nv75+&nbsp;<strong className="text-content">{entry.pokemon75Plus}</strong>
                  </span>
                  <span className="text-content/50 text-xs">
                    IV≥25&nbsp;<strong className="text-content">{entry.highIvPokemon}</strong>
                  </span>
                  <span className="text-content/50 text-xs">
                    Bonheur&nbsp;<strong className="text-content">{entry.happyPokemon}</strong>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Leaderboard (main view) ──────────────────────────────────────────────────

type Tab = 'pokedex' | 'prestige';

export const Leaderboard = () => {
  const { meUser } = useMeUser();
  const [tab, setTab] = useState<Tab>('pokedex');

  return (
    <main className="min-h-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-5">
        <div>
          <h1 className="font-lexend text-content text-2xl font-bold">Classements</h1>
          <p className="text-content/50 mt-0.5 text-sm">
            Compare tes performances avec les autres joueurs
          </p>
        </div>

        <GenericCard
          title={tab === 'pokedex' ? 'Classement Pokédex' : 'Classement Prestige'}
          subtitle={
            tab === 'pokedex'
              ? 'Nombre d\'entrées dans le Pokédex'
              : 'Score de prestige sur 1 000 points'
          }
        >
          {/* Tab switcher */}
          <div className="mb-5 flex gap-2 rounded-2xl bg-raised p-1">
            <button
              onClick={() => setTab('pokedex')}
              className={`flex-1 rounded-xl py-2 text-sm font-semibold transition-all ${
                tab === 'pokedex'
                  ? 'bg-surface text-content shadow-sm'
                  : 'text-content/50 hover:text-content'
              }`}
            >
              Pokédex
            </button>
            <button
              onClick={() => setTab('prestige')}
              className={`flex-1 rounded-xl py-2 text-sm font-semibold transition-all ${
                tab === 'prestige'
                  ? 'bg-surface text-content shadow-sm'
                  : 'text-content/50 hover:text-content'
              }`}
            >
              Prestige
            </button>
          </div>

          {tab === 'pokedex' ? (
            <PokedexLeaderboard currentUsername={meUser?.username} />
          ) : (
            <PrestigeLeaderboard currentUsername={meUser?.username} />
          )}
        </GenericCard>
      </div>
    </main>
  );
};
